import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import MoonphaseThemeToggle from "@/components/MoonphaseThemeToggle";
import { ROLE_LABELS } from "@/lib/format";
import {
  LayoutDashboard, Watch, Crown, Search, Tag, FileText, CheckSquare,
  ShoppingBag, Store, BookOpen, Newspaper, Bell, Users, LogOut, Menu, X,
  LayoutTemplate, Wrench,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/cms", label: "Site Pages", icon: LayoutTemplate },
  { to: "/admin/watches", label: "Watches", icon: Watch },
  { to: "/admin/parts", label: "Parts", icon: Wrench },
  { to: "/admin/leads", label: "VIP Leads", icon: Crown },
  { to: "/admin/requests", label: "Watch Requests", icon: Search },
  { to: "/admin/sellers", label: "Sell / Consign", icon: Tag },
  { to: "/admin/dossiers", label: "Dossiers", icon: FileText },
  { to: "/admin/merch", label: "Merch", icon: Store },
  { to: "/admin/journal", label: "Journal", icon: Newspaper },
  { to: "/admin/about", label: "About Page", icon: BookOpen },
  { to: "/admin/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/users", label: "Team & Roles", icon: Users, ownerOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => !n.ownerOnly || user?.role === "owner");

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const SidebarInner = (
    <div className="flex h-full flex-col bg-[#0d0d0c] text-[#e8e3d8]">
      <div className="px-6 py-6 border-b border-[#262420]">
        <div className="text-[#c9a86a] tracking-[0.25em] text-sm font-semibold">LE COLLECTIF</div>
        <div className="text-[#c9a86a] tracking-[0.25em] text-sm font-semibold">HORLOGER</div>
        <div className="text-[10px] text-[#6f6a5f] mt-1 tracking-wider">ADMIN · AN ATELIER DUGAS HOUSE</div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {items.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#1c1a16] text-[#c9a86a] border-l-2 border-[#c9a86a]"
                    : "text-[#a8a294] hover:bg-[#16140f] hover:text-[#e8e3d8]"
                }`
              }
            >
              <Icon size={17} />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-[#262420] p-4">
        <div className="text-sm font-medium text-[#e8e3d8]">{user?.name}</div>
        <div className="text-xs text-[#c9a86a]">{ROLE_LABELS[user?.role] || user?.role}</div>
        <button
          onClick={doLogout}
          data-testid="admin-logout-btn"
          className="mt-3 flex items-center gap-2 text-xs text-[#a8a294] hover:text-[#e8e3d8]"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:block w-64 shrink-0 fixed inset-y-0 left-0">{SidebarInner}</aside>
      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64">{SidebarInner}</div>
        </div>
      )}
      <div className="flex-1 md:ml-64 min-w-0">
        <header className="flex items-center justify-between gap-3 bg-[var(--panel)] border-b border-[var(--border-lch)] px-4 md:px-8 py-3 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="md:hidden text-[var(--text)]"><Menu size={22} /></button>
            <span className="md:hidden text-[var(--gold)] tracking-[0.2em] text-xs">LCH ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] tracking-[0.2em] uppercase text-[var(--muted)]">Appearance</span>
            <span className="hidden sm:block h-7 w-px bg-gradient-to-b from-transparent via-[var(--border-lch)] to-transparent" aria-hidden="true" />
            <MoonphaseThemeToggle size="h-9" className="shrink-0" />
          </div>
        </header>
        <div className="h-px bg-gradient-to-r from-transparent via-[var(--gold)]/30 to-transparent" aria-hidden="true" />
        <main className="p-4 md:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
